import { Check } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { filterFontCatalogFamilies } from "@/lib/fontCatalog";
import { fontFamilyPreviewSvg } from "@/lib/fontSvgPreviews";
import { loadGoogleFontWeights } from "@/lib/GoogleFontLoader";
import { cn } from "@/lib/utils";
import { DraggablePanel } from "./DraggablePanel";

export function FontPickerPopover({
  appliedFamily,
  onPreview,
  onCommit,
  onClose,
}: {
  appliedFamily: string;
  onPreview: (family: string) => void;
  onCommit: (family: string) => void;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [query, setQuery] = useState("");
  const [highlightedFamily, setHighlightedFamily] = useState(appliedFamily);
  const onPreviewRef = useRef(onPreview);
  const onCommitRef = useRef(onCommit);
  const onCloseRef = useRef(onClose);
  const highlightedFamilyRef = useRef(highlightedFamily);

  onPreviewRef.current = onPreview;
  onCommitRef.current = onCommit;
  onCloseRef.current = onClose;
  highlightedFamilyRef.current = highlightedFamily;

  const families = useMemo(() => filterFontCatalogFamilies(query), [query]);

  useEffect(() => {
    searchRef.current?.focus();
    searchRef.current?.select();
    setHighlightedFamily(appliedFamily);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollFamilyToCenter(appliedFamily));
    });
  }, [appliedFamily]);

  useEffect(() => {
    if (families.length === 0) {
      return;
    }
    if (!families.includes(highlightedFamilyRef.current)) {
      setHighlightedFamily(families[0]!);
    }
  }, [families]);

  useEffect(() => {
    void loadGoogleFontWeights(highlightedFamily);
    onPreviewRef.current(highlightedFamily);
  }, [highlightedFamily]);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || panelRef.current?.contains(target)) {
        return;
      }
      onCloseRef.current();
    }

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.target instanceof HTMLInputElement && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
        return;
      }

      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveHighlight(1);
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveHighlight(-1);
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        onCommitRef.current(highlightedFamilyRef.current);
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <DraggablePanel
      panelRef={panelRef}
      storageKey="font-picker"
      title="Fonts"
      widthClass="w-72"
      onClose={onClose}
    >
      <div className="space-y-2">
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              moveHighlight(event.key === "ArrowDown" ? 1 : -1);
            }
          }}
          placeholder="Search fonts"
          className="h-8 w-full rounded border border-white/10 bg-white/[0.04] px-2 text-[12px] text-slate-100 outline-none placeholder:text-slate-500 focus:border-sky-500/40"
        />
        <div ref={listRef} className="max-h-72 overflow-y-auto rounded border border-white/10">
          {families.length === 0 ? (
            <div className="px-2 py-3 text-center text-[11px] text-slate-500">No matching fonts.</div>
          ) : (
            families.map(family => {
              const isApplied = family === appliedFamily;
              const isHighlighted = family === highlightedFamily;
              return (
                <button
                  key={family}
                  ref={element => {
                    if (element) {
                      rowRefs.current.set(family, element);
                    } else {
                      rowRefs.current.delete(family);
                    }
                  }}
                  type="button"
                  onMouseEnter={() => setHighlightedFamily(family)}
                  onClick={() => onCommit(family)}
                  aria-label={family}
                  title={family}
                  className={cn(
                    "flex h-9 w-full items-center gap-2 px-2 text-left text-[12px] text-slate-200 hover:bg-white/[0.06]",
                    isHighlighted && "bg-sky-500/20 text-sky-100",
                  )}
                >
                  <span className="grid size-4 shrink-0 place-items-center text-sky-300">
                    {isApplied ? <Check className="size-3.5" aria-hidden="true" /> : null}
                  </span>
                  <FontFamilyPreview family={family} />
                </button>
              );
            })
          )}
        </div>
      </div>
    </DraggablePanel>
  );

  function moveHighlight(delta: number): void {
    if (families.length === 0) {
      return;
    }

    const currentIndex = Math.max(0, families.indexOf(highlightedFamily));
    const nextIndex = Math.min(families.length - 1, Math.max(0, currentIndex + delta));
    const nextFamily = families[nextIndex];
    if (!nextFamily) {
      return;
    }

    setHighlightedFamily(nextFamily);
    requestAnimationFrame(() => {
      const row = rowRefs.current.get(nextFamily);
      const list = listRef.current;
      if (!row || !list) {
        return;
      }

      const rowTop = row.offsetTop;
      const rowBottom = rowTop + row.offsetHeight;
      if (rowTop < list.scrollTop) {
        list.scrollTop = rowTop;
      } else if (rowBottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = rowBottom - list.clientHeight;
      }
    });
  }

  function scrollFamilyToCenter(family: string): void {
    const row = rowRefs.current.get(family);
    const list = listRef.current;
    if (!row || !list) {
      return;
    }
    list.scrollTop = row.offsetTop - list.clientHeight / 2 + row.clientHeight / 2;
  }
}

export function FontFamilyPreview({
  family,
  fallbackLabel = family,
}: {
  family: string;
  fallbackLabel?: string;
}) {
  const previewSource = fontFamilyPreviewSvg(family);

  if (!previewSource) {
    return <span className="min-w-0 truncate">{fallbackLabel}</span>;
  }

  return (
    <span className="flex min-w-0 flex-1 items-center">
      <img
        src={previewSource}
        alt=""
        aria-hidden="true"
        className="h-4 max-w-full object-contain object-left opacity-90 [filter:invert(1)]"
        draggable={false}
      />
      <span className="sr-only">{family}</span>
    </span>
  );
}
